import React, { useState, useEffect, useRef } from "react";
import { useProjects } from "../../context/ProjectContext";
import { useTasks } from "../../context/TaskContext";
import { toast } from "sonner";
import Swal from "sweetalert2";

export const EditProjectModal = ({ isOpen, onClose, project, onProjectDeleted }) => {
    const { updateProject, deleteProject } = useProjects();
    const { deleteProjectTasks } = useTasks();

    const [projectName, setProjectName] = useState("");
    const [selectedIcon, setSelectedIcon] = useState("📁");
    const [selectedColor, setSelectedColor] = useState("#64748b");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const modalRef = useRef(null);

    const quickIcons = ["📁", "📚", "💼", "🚀", "🏡", "🧠", "🎯", "🛒"];
    const quickColors = ["#64748b", "#ef4444", "#f97316", "#3b82f6", "#10b981", "#a855f7", "#ec4899", "#eab308"];

    // Precargar datos del proyecto seleccionado al abrir el modal
    useEffect(() => {
        if (project) {
            setProjectName(project.name || "");
            setSelectedIcon(project.icon || "📁");
            setSelectedColor(project.color || "#64748b");
        }
    }, [project]);

    // Cerrar al hacer clic fuera del recuadro
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    if (!isOpen || !project) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!projectName.trim()) {
            toast.error("El nombre del proyecto no puede estar vacío.");
            return;
        }

        setSaving(true);
        try {
            await updateProject(project.id, {
                name: projectName.trim(),
                icon: selectedIcon,
                color: selectedColor
            });

            toast.success("¡Proyecto actualizado correctamente!");
            onClose();
        } catch (error) {
            toast.error(`Error al editar el proyecto: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        Swal.fire({
            html: `
                <div class="flex flex-col items-center pt-2 pb-1">
                    <div class="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500 mb-3 shadow-inner">
                        <i class="fas fa-trash-alt text-2xl"></i>
                    </div>
                    <h3 class="text-xl font-extrabold text-slate-800 tracking-tight">¿Eliminar proyecto?</h3>
                    <p class="text-xs text-slate-500 mt-1.5 leading-relaxed text-center">
                        Se eliminará el proyecto <span class="font-bold text-slate-700">"${project.name}"</span> y <strong class="text-red-600">todas sus tareas asociadas</strong>.
                    </p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: '<i class="fas fa-trash-alt mr-1.5"></i> Sí, eliminar todo',
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            reverseButtons: true,
            padding: "1.5rem",
            backdrop: `rgba(15, 23, 42, 0.45) backdrop-filter backdrop-blur-sm`,
            customClass: {
                popup: "rounded-3xl shadow-2xl border border-slate-100 bg-white max-w-sm",
                confirmButton: "px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-md shadow-red-500/25 transition-all duration-150 active:scale-95 ml-2 cursor-pointer",
                cancelButton: "px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-150 active:scale-95 cursor-pointer"
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setDeleting(true);
                try {
                    await deleteProject(project.id);
                    deleteProjectTasks(project.id);
                    if (onProjectDeleted) {
                        onProjectDeleted(project.id);
                    }
                    toast.success("Proyecto y tareas eliminados correctamente");
                    onClose();
                } catch (error) {
                    toast.error(`Error al eliminar el proyecto: ${error.message}`);
                } finally {
                    setDeleting(false);
                }
            }
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"></div>

            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 relative z-10 animate-fadeIn"
            >
                <div className="flex items-center space-x-2 mb-4">
                    <i className="far fa-edit text-blue-600 text-base"></i>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Editar Proyecto</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Nombre</label>
                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-medium bg-slate-50 focus:bg-white transition-all"
                            maxLength={40}
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Ícono</label>
                        <div className="flex flex-wrap gap-2">
                            {quickIcons.map(icon => (
                                <button
                                    key={icon}
                                    type="button"
                                    onClick={() => setSelectedIcon(icon)}
                                    className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm border transition-all
                                        ${selectedIcon === icon ? "bg-blue-50 border-blue-400 scale-105" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1.5">Color</label>
                        <div className="flex flex-wrap gap-2.5">
                            {quickColors.map(color => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setSelectedColor(color)}
                                    className="w-6 h-6 rounded-full transition-transform relative flex items-center justify-center hover:scale-110 shadow-sm"
                                    style={{ backgroundColor: color }}
                                >
                                    {selectedColor === color && (
                                        <i className="fas fa-check text-[10px] text-white"></i>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={saving || deleting}
                            className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors flex items-center space-x-1.5"
                        >
                            <i className="far fa-trash-alt text-xs"></i>
                            <span>{deleting ? "Eliminando..." : "Eliminar proyecto"}</span>
                        </button>

                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={saving || deleting || !projectName.trim()}
                                className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-md transition-colors
                                    ${saving || deleting || !projectName.trim() ? "bg-blue-300 cursor-not-allowed shadow-none" : "bg-blue-600 hover:bg-blue-700"}`}
                            >
                                {saving ? "Guardando..." : "Guardar Cambios"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};